import { useEffect, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import {
  AuthActions,
  AuthField,
  AuthNotice,
  AuthPage,
  PasswordInput,
  SubmitButton,
  authKitTextKeys,
  showResult,
} from "../../auth/authKit";
import { changePendingPassword, readPendingChange } from "./accountApi";
import { useFocusHeading } from "./focusHeading";

/** Claves de texto de la pantalla (las que lee CambiarPassword.razor), el aviso de falla técnica y las del kit. */
export const changePasswordTextKeys = [
  ...authKitTextKeys,
  "PWD_",
  "LOGIN_TITLE",
  "LOGIN_SUBMIT",
  "LOGIN_ERROR",
  "CP_PASSWORD_RULE",
  "ERROR_TEXT",
];

/**
 * Cambio de clave obligatorio o vencido (/cambiarpassword?token=, CambiarPassword.razor), sin sesión, con las piezas del
 * kit: la persona pendiente viaja firmada en el token que dejó el ingreso. El servidor ya respondió 302 al login si el
 * token no sirve; aquí se lee el motivo («expired» u obligatorio) y el enlace de vuelta, que conserva solo un ReturnUrl
 * local. Volver al login es el atrás del kit (C03), en la fila del título.
 */
export function ChangePasswordPage({ L }: { L: (key: string) => string }) {
  const [params] = useSearchParams();
  const token = params.get("token");
  const returnUrl = params.get("ReturnUrl");
  const [pending, setPending] = useState<{ reason: string; loginHref: string } | null>(null);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Clave actual incorrecta y regla de la nueva: cada una bajo su campo (C05).
  const [fields, setFields] = useState<{ current?: string; nuevo?: string }>({});
  const [attempt, setAttempt] = useState(0);
  const [sending, setSending] = useState(false);
  const current = useRef<HTMLInputElement>(null);
  const fresh = useRef<HTMLInputElement>(null);
  const busy = useRef(false);
  const title = L("PWD_CHANGE_TITLE");
  useEffect(() => {
    document.title = title;
  }, [title]);
  useFocusHeading(pending !== null);
  useEffect(() => {
    void readPendingChange(token, returnUrl).then((result) => {
      if ("redirect" in result) window.location.assign(result.redirect);
      else setPending(result);
    });
  }, [token, returnUrl]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy.current) return;
    busy.current = true;
    setSending(true);
    let leaving = false;
    try {
      const result = await changePendingPassword({
        token,
        currentPassword: current.current?.value ?? "",
        newPassword: fresh.current?.value ?? "",
        returnUrl,
      });
      if (result.status === "redirect") {
        leaving = true;
        window.location.assign(result.next ?? "/login");
        return;
      }
      if (current.current) current.current.value = "";
      if (fresh.current) fresh.current.value = "";
      const key = result.messageKey ?? "PWD_ERR_GENERIC";
      const field = key === "LOGIN_ERROR" ? "current" : key === "CP_PASSWORD_RULE" ? "nuevo" : null;
      showResult(
        () => {
          if (result.status === "done") setDone(true);
          setFields(result.status !== "done" && field ? { [field]: L(key) } : {});
          setError(result.status !== "done" && !field ? L(key) : null);
          setAttempt((value) => value + 1);
        },
        result.status !== "done" && field !== null,
      );
    } finally {
      if (!leaving) {
        busy.current = false;
        setSending(false);
      }
    }
  };

  if (!pending) return null;
  return (
    <AuthPage title={title} back={{ href: pending.loginHref, label: L("LOGIN_TITLE") }}>
      {done ? (
        <>
          <AuthNotice tone="done">{L("PWD_CHANGED")}</AuthNotice>
          <AuthActions>
            <a className="tm-btn primary" href={pending.loginHref}>
              {L("LOGIN_SUBMIT")}
            </a>
          </AuthActions>
        </>
      ) : (
        <>
          <AuthNotice tone="warning">{L(pending.reason === "expired" ? "PWD_EXPIRED" : "PWD_MUST_CHANGE")}</AuthNotice>
          {error ? (
            <AuthNotice key={attempt} tone="error">
              {error}
            </AuthNotice>
          ) : null}
          <form method="post" onSubmit={(event) => void submit(event)}>
            <AuthField id="current" label={L("PWD_CURRENT")} error={fields.current}>
              <PasswordInput
                id="current"
                name="form.CurrentPassword"
                autoComplete="current-password"
                inputRef={current}
                error={fields.current}
                required
                L={L}
              />
            </AuthField>
            <AuthField id="nuevo" label={L("PWD_NEW")} error={fields.nuevo} help={L("CP_PASSWORD_RULE")}>
              <PasswordInput
                id="nuevo"
                name="form.NewPassword"
                autoComplete="new-password"
                inputRef={fresh}
                error={fields.nuevo}
                help={L("CP_PASSWORD_RULE")}
                required
                L={L}
              />
            </AuthField>
            <AuthActions>
              <SubmitButton sending={sending}>{L("PWD_SAVE")}</SubmitButton>
            </AuthActions>
          </form>
        </>
      )}
    </AuthPage>
  );
}
